import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

const emptyForm = {
  device_mode: 'existing',
  device_internal_id: '',
  device_id: '',
  imei_number: '',
  sim_number: '',
  type: 'GPS Tracker',
  model: '',
  manufacturer: '',
  agency_id: '',
  school_id: '',
  parent_first_name: '',
  parent_last_name: '',
  parent_phone: '',
  parent_email: '',
  parent_password: '',
  student_first_name: '',
  student_last_name: '',
  standard: '1',
  division: 'A',
  roll_number: '1',
  activate_subscription: true,
  plan_id: '',
  payment_method: 'cash'
};

const DeviceActivation = ({ schools = [], agencies = [], onDataChanged }) => {
  const [form, setForm] = useState(emptyForm);
  const [devices, setDevices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
    setSuccess(null);
  };

  const filteredSchools = useMemo(() => {
    if (!form.agency_id) return schools;
    const agencyId = Number(form.agency_id);
    return schools.filter(
      (s) => !s.agency_id || Number(s.agency_id) === agencyId
    );
  }, [schools, form.agency_id]);

  const loadDevices = useCallback(async () => {
    try {
      setLoadingDevices(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const params = new URLSearchParams();
      if (form.school_id) params.set('school_id', form.school_id);

      const response = await fetch(
        `${API_BASE_URL}/admin/marketing-assign/available-devices?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load available devices');
      }
      setDevices(Array.isArray(data.items) ? data.items : []);
      setForm((prev) => {
        if (!prev.device_internal_id) return prev;
        const stillThere = (data.items || []).some((d) => d.id === prev.device_internal_id);
        return stillThere ? prev : { ...prev, device_internal_id: '' };
      });
    } catch (err) {
      setDevices([]);
      setError(err.message || 'Failed to load available devices');
    } finally {
      setLoadingDevices(false);
    }
  }, [form.school_id]);

  const loadPlans = useCallback(async () => {
    if (!form.school_id) {
      setPlans([]);
      return;
    }
    try {
      setLoadingPlans(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const params = new URLSearchParams({ school_id: String(form.school_id) });
      const response = await fetch(`${API_BASE_URL}/admin/device-activation/plans?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to load plans');
      }
      const items = Array.isArray(data.items) ? data.items : [];
      setPlans(items);
      setForm((prev) => {
        if (prev.plan_id || items.length === 0) return prev;
        return { ...prev, plan_id: String(items[0].id) };
      });
    } catch (err) {
      setPlans([]);
      console.warn(err.message);
    } finally {
      setLoadingPlans(false);
    }
  }, [form.school_id]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      setSuccess(null);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const payload = {
        ...form,
        school_id: form.school_id ? Number(form.school_id) : null,
        agency_id: form.agency_id ? Number(form.agency_id) : null,
        roll_number: Number(form.roll_number) || 0,
        plan_id: form.plan_id ? Number(form.plan_id) : null,
        activate_subscription: Boolean(form.activate_subscription),
        imei_number: form.imei_number || form.device_id,
        device_id: form.device_id || form.imei_number
      };

      const response = await fetch(`${API_BASE_URL}/admin/device-activation`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Device activation failed');
      }

      setSuccess(data);
      setForm((prev) => ({
        ...emptyForm,
        school_id: prev.school_id,
        agency_id: prev.agency_id,
        standard: prev.standard,
        division: prev.division,
        activate_subscription: prev.activate_subscription,
        plan_id: prev.plan_id,
        payment_method: prev.payment_method
      }));
      await loadDevices();
      if (typeof onDataChanged === 'function') {
        onDataChanged();
      }
    } catch (err) {
      setError(err.message || 'Device activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Full Device Activation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One form for admin: add/select device → optional agency → school → parent → student →
          assign device. Optionally activate subscription (same as school collect-payment).
          Agency is optional — admin can assign directly to school.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            <Typography variant="subtitle2">{success.message}</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Parent phone: <strong>{success.parent?.phone}</strong>
              {success.parent?.password ? (
                <> · password: <strong>{success.parent.password}</strong></>
              ) : null}
            </Typography>
            <Typography variant="body2">
              Student: {success.student?.name} · Device: {success.device?.deviceId}
              {success.school?.name ? ` · School: ${success.school.name}` : ''}
            </Typography>
            {success.subscriptionActivated && success.subscription && (
              <Typography variant="body2">
                Subscription: {success.subscription.planName} until {success.subscription.endDate}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {success.loginHint}
            </Typography>
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Device
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Device source</InputLabel>
                <Select
                  label="Device source"
                  value={form.device_mode}
                  onChange={(e) => updateField('device_mode', e.target.value)}
                >
                  <MenuItem value="existing">Use existing inventory device</MenuItem>
                  <MenuItem value="new">Add new device now</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.device_mode === 'existing' ? (
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Available device</InputLabel>
                  <Select
                    label="Available device"
                    value={form.device_internal_id}
                    onChange={(e) => updateField('device_internal_id', e.target.value)}
                    disabled={loadingDevices}
                  >
                    {devices.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {loadingDevices && (
                  <Typography variant="caption" color="text.secondary">
                    Loading devices…
                  </Typography>
                )}
              </Grid>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="IMEI / Device ID"
                    value={form.imei_number}
                    onChange={(e) => {
                      updateField('imei_number', e.target.value);
                      updateField('device_id', e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="SIM number"
                    value={form.sim_number}
                    onChange={(e) => updateField('sim_number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Model"
                    value={form.model}
                    onChange={(e) => updateField('model', e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            2. Agency (optional) + School (required)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Agency (optional)</InputLabel>
                <Select
                  label="Agency (optional)"
                  value={form.agency_id}
                  onChange={(e) => {
                    updateField('agency_id', e.target.value);
                    updateField('school_id', '');
                  }}
                >
                  <MenuItem value="">No agency — assign direct to school</MenuItem>
                  {agencies.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>School</InputLabel>
                <Select
                  label="School"
                  value={form.school_id}
                  onChange={(e) => updateField('school_id', e.target.value)}
                >
                  {filteredSchools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            3. Parent login
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="First name"
                value={form.parent_first_name}
                onChange={(e) => updateField('parent_first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Last name"
                value={form.parent_last_name}
                onChange={(e) => updateField('parent_last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Phone"
                value={form.parent_phone}
                onChange={(e) => updateField('parent_phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Password"
                type="text"
                value={form.parent_password}
                onChange={(e) => updateField('parent_password', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Email (optional)"
                value={form.parent_email}
                onChange={(e) => updateField('parent_email', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            4. Student
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="First name"
                value={form.student_first_name}
                onChange={(e) => updateField('student_first_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                required
                label="Last name"
                value={form.student_last_name}
                onChange={(e) => updateField('student_last_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Class / Standard"
                value={form.standard}
                onChange={(e) => updateField('standard', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Division"
                value={form.division}
                onChange={(e) => updateField('division', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Roll no"
                value={form.roll_number}
                onChange={(e) => updateField('roll_number', e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            5. Subscription (optional)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.activate_subscription)}
                    onChange={(e) => updateField('activate_subscription', e.target.checked)}
                  />
                }
                label="Activate subscription now"
              />
            </Grid>
            {form.activate_subscription && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Plan</InputLabel>
                    <Select
                      label="Plan"
                      value={form.plan_id}
                      onChange={(e) => updateField('plan_id', e.target.value)}
                      disabled={loadingPlans}
                    >
                      {plans.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name} · {p.durationDays ?? p.durationMonths} days · ₹{p.baseAmount ?? p.price}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment method</InputLabel>
                    <Select
                      label="Payment method"
                      value={form.payment_method}
                      onChange={(e) => updateField('payment_method', e.target.value)}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="upi">UPI</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="bank_transfer">Bank transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {submitting ? 'Activating…' : 'Activate Device (Full Process)'}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default DeviceActivation;
